/**
 * WorkQueueContext — the "Next up" session (docs/crm-work-queue-plan.md).
 *
 * One provider above the CRM routes owns the whole guided flow: start()
 * fetches GET /api/crm/work-queue and navigates to item 1; done()/skip()
 * advance and navigate; when the list runs dry, ONE silent refill (minus the
 * leads this session already touched) — still dry means the celebration
 * state, and exit() lands back on Today. State persists in sessionStorage
 * (per-tab), so a mid-run refresh keeps the bar; detours are fine — the bar
 * floats over every CRM page and simply waits (NextUpBar renders it).
 *
 * OUTREACH LISTS (docs/crm-outreach-lists-plan.md §B6) ride the same session
 * as a third source, `call_list`, started by startList() — start() is left
 * exactly as it was. What differs is how a list session MOVES:
 *   - it advances on a logged outcome (afterOutcome), never on a bare "Done",
 *     and only after the write has landed — advancing past a failed write
 *     would silently lose the try;
 *   - every advance re-fetches `?list=` and folds the answer in with
 *     applyListRefetch, because the list changes under the recruiter all day
 *     (a reply jumps the line, a next try comes due);
 *   - while it is open a 60s poll keeps the counts and the "new reply — up
 *     next" chip honest, and NEVER navigates — a recruiter mid-call must not
 *     be moved by a timer. An inbound text from one of my list leads pokes
 *     the poll early (OUTREACH_REPLY_EVENT, fired by the SMS toast feed).
 *
 * RECRUITMENT LISTS (docs/plans/crm-recruitment-list-plan.md B5) are a fourth
 * source, `study`, started by startRows() from a study's sheet ("Work
 * through"). The sheet hands over the rows on screen, already built into
 * queue items in its order, and the session walks them the way a normal one
 * does: Done, Skip and Snooze advance it, and a call logged from the bar's
 * attempt menu does NOT (a call often has two outcomes, "Talked" then a
 * status change). When the rows run out, its one silent refill re-reads the
 * sheet with the filters it started from, minus everyone the session already
 * worked or skipped; still dry is the finished state, with the way back to
 * the sheet. start() and startList() are untouched.
 *
 * ATTENTION TIERS (backend services/crmAttention.js; owner decision 2026-09-28:
 * "urgent first, then offer the rest"). start() and a study session serve the
 * NEEDS-ACTION-NOW tier by default (`tier: 'now'`); the server's tier_counts
 * (or, for a study, the sheet's own rows) say how many KEEP-TRYING leads are
 * left, and when the urgent ones run out the finished state offers them:
 * startKeepTrying() starts the same kind of session on the `later` tier. An
 * outreach-list session is untouched: the list is the work, whatever its tier.
 *
 * All the math lives in workQueueLogic (pure, tested); this file is only the
 * React shell: fetch, navigate, persist.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  advanceState,
  applyListRefetch,
  isWorkQueueTier,
  callingHoursBlock,
  currentItem,
  freshCount,
  isListParam,
  isStudyRef,
  listNameFor,
  markOutcomeLogged,
  newReplyCount,
  orderOwnWorkFirst,
  doneCount,
  rowStatus,
  ownersParam,
  parseStoredState,
  progress,
  serializeState,
  sessionExcludes,
  skipState,
  startRowsState,
  studyLabelFor,
  studyReturnHref,
  withRefill,
  OUTREACH_REPLY_EVENT,
  type OwnerSelection,
  type QueueRowStatus,
  type WorkQueueItem,
  type WorkQueueSource,
  type WorkQueueState,
  type WorkQueueStudyFilters,
  type WorkQueueStudyRef,
  type WorkQueueTier,
} from './workQueueLogic';
import type { WorkQueueListCounts } from './outreach/types';
import { loadAllRecruitRows } from './recruit/api';
import { compareRows, rowsToQueueItems } from './recruit/recruitLogic';
import type { RecruitRowsParams } from './recruit/types';

const STORE_KEY = 'crm.workQueue.v1';

/** How often an open list session re-checks the list (counts + new replies). */
const LIST_POLL_MS = 60_000;

/** sessionStorage can throw (private windows, blocked site data) — never let
 *  that take the bar down; it only costs the refresh-survives-state nicety. */
function readStored(): WorkQueueState | null {
  try { return parseStoredState(window.sessionStorage.getItem(STORE_KEY)); } catch { return null; }
}
function writeStored(s: WorkQueueState | null): void {
  try {
    if (s) window.sessionStorage.setItem(STORE_KEY, serializeState(s));
    else window.sessionStorage.removeItem(STORE_KEY);
  } catch { /* storage blocked — the session still runs in memory */ }
}

/**
 * What a study session's refill reads when it has no stored filters: the
 * sheet's own defaults. Everyone on the study (decision 3), open leads,
 * nothing searched, only the rows that need something now.
 */
const DEFAULT_STUDY_FILTERS: WorkQueueStudyFilters = {
  owners: [],
  scope: 'open',
  statuses: null,
  q: null,
  showArchived: false,
  onlyNeedsAction: false,
  includeWaiting: false,
  tier: 'now',
};

/** The tier a session serves: a study session's filters' (legacy includeWaiting = all), else its own. */
function tierOf(s: WorkQueueState | null | undefined): WorkQueueTier {
  if (!s) return 'now';
  if (s.source === 'call_list') return 'all';
  if (s.source === 'study') {
    const f = s.studyFilters;
    if (f?.includeWaiting && !f.onlyNeedsAction) return 'all';
    return isWorkQueueTier(f?.tier) ? f!.tier! : 'now';
  }
  return isWorkQueueTier(s.tier) ? s.tier : 'now';
}

/**
 * The rows query for a study session's filters. It is the sheet's own query
 * (recruit/RecruitSheet.tsx rowsQuery; keep them in step), so the refill reads
 * exactly what the sheet showed: the owners always explicit ('all' is everyone
 * on the study), a status list only when chips were picked (an empty one would
 * match nothing), archived leads only when asked for.
 */
function studyRowsParams(
  f: WorkQueueStudyFilters,
  meId: string | null,
): Omit<RecruitRowsParams, 'limit' | 'offset'> {
  const params: Omit<RecruitRowsParams, 'limit' | 'offset'> = {
    owners: ownersParam(f.owners, meId),
    scope: f.scope,
  };
  if (f.statuses && f.statuses.length) params.status = f.statuses.join(',');
  if (f.q) params.q = f.q;
  if (f.showArchived) params.show_archived = true;
  return params;
}

/** The session would come back from sessionStorage, so a refresh cannot drop it. */
function survivesRefresh(s: WorkQueueState): boolean {
  try { return parseStoredState(serializeState(s)) !== null; } catch { return false; }
}

interface WorkQueueCtx {
  /** A session is running (the bar is up — including the finished state). */
  active: boolean;
  /** The list ran dry after a refill — celebration state until exit(). */
  finished: boolean;
  starting: boolean;
  item: WorkQueueItem | null;
  position: number;
  total: number;
  /** The whole queue, each row tagged done / skipped / current / upcoming —
   *  what the bar's expandable list renders. */
  rows: { item: WorkQueueItem; status: QueueRowStatus }[];
  /** How many rows have been worked (the list's progress line). */
  done_count: number;
  /** Jump straight to a row without marking anything. */
  goTo: (index: number) => void;
  /**
   * Start a session; resolves to the queue size (0 = nothing to work).
   * `owners` is the picker's selection — omitted means the server's default
   * (the caller's leads + unassigned); [] means network-wide. `tier` is the
   * attention tier: `now` (the default: needs action now) or `later` (keep trying).
   */
  start: (source?: 'safety_net', owners?: OwnerSelection, tier?: WorkQueueTier) => Promise<number>;
  /** The attention tier the running session serves (`now` when none runs). */
  sessionTier: WorkQueueTier;
  /**
   * Keep-trying leads left when the session last read its queue: the finished
   * state's "Keep trying (N)" offer. null = unknown, or not a `now` session.
   */
  keepTryingCount: number | null;
  /**
   * The finished state's "Keep trying": the same kind of session (the whole
   * queue, the safety net, or this study's sheet) on the keep-trying tier.
   * Resolves to the queue size; 0 = nothing was started (the finished state stays).
   */
  startKeepTrying: () => Promise<number>;
  done: () => void;
  skip: () => void;
  /**
   * Defer the lead on deck until `untilIso`, then advance as if Done. Unlike
   * done(), this one PERSISTS: it writes crm.contacts.snoozed_until, so the
   * lead stays off the queue across sessions until the snooze passes (or the
   * patient replies, which clears it server-side).
   */
  snoozeCurrent: (untilIso: string) => void;
  snoozing: boolean;
  /** Set when the snooze PATCH failed — the bar stays on the lead. */
  snoozeError: string | null;
  /**
   * End the session. `true` lands on Today (the celebration's button); a path
   * lands there instead (a list session's "Back to the list").
   */
  exit: (goHome?: boolean | string) => void;

  // --- Outreach lists (docs/crm-outreach-lists-plan.md §B6) ----------------

  /** Which kind of session is running (null = none). */
  source: WorkQueueSource | null;
  /** A list session is running ("Working: <list name>"). */
  isListSession: boolean;
  /** The list being worked: a uuid, or 'mine' for every list I'm on. */
  listId: string | null;
  /** The list's name; null for several lists at once. */
  listName: string | null;
  /**
   * Work through an outreach list: GET /work-queue?list=<id|mine>, then land
   * on the first person. Resolves to how many people are up now; 0 means
   * nobody is servable (the session is NOT started) — read `listCounts` for
   * why ("next tries start Tue 9:00"). A failed fetch also resolves 0, with
   * `listCounts` null. While a session on ANOTHER list is running, a start
   * that comes back empty or fails leaves `listCounts` as that session's.
   * Omitted = 'mine'.
   */
  startList: (listId?: string | 'mine') => Promise<number>;
  /** The last `list_counts` the server sent for a list session / startList. */
  listCounts: WorkQueueListCounts | null;
  /**
   * Call ONLY after an outcome write has succeeded. Records the lead as
   * worked and moves the session on: a list session re-fetches and lands on
   * the new top; a normal session just advances. `navigate: false` updates
   * the bar without leaving the current page (Call Mode without "Log & next").
   * Outside calling hours, without the recruiter's click-through, the bar
   * moves but the page does not (see outsideHoursAck).
   */
  afterOutcome: (contactId: string, opts?: { navigate?: boolean }) => Promise<void>;
  /** Replies the last poll found that the session isn't showing yet. */
  newReplies: number;
  /** Finished state: people the last poll found who could be worked now. */
  freshReady: number;
  /** Finished state's "Keep going": fold the last poll in and resume. */
  resumeList: () => void;
  /**
   * Finished state's "Go back to them": forget this sitting's skips, re-fetch
   * the list and land on the first person who is due — the people skipped
   * included.
   */
  resumeSkipped: () => void;
  /**
   * The recruiter clicked through "calling now needs a reason" this sitting.
   * Outcomes logged while it is set, outside 8am–9pm, carry outside_hours.
   */
  outsideHoursAck: boolean;
  /** Acknowledge working outside calling hours; performs a held advance. */
  ackOutsideHours: () => void;
  /** An outcome landed outside hours before the acknowledgement — the bar has
   *  moved on, the page has not, until ackOutsideHours(). */
  advanceHeld: boolean;

  // --- Recruitment lists (docs/plans/crm-recruitment-list-plan.md B5) -----

  /** A study session is running ("Working: <study> recruitment list"). */
  isStudySession: boolean;
  /** The study being worked (its uuid); null outside a study session. */
  studyId: string | null;
  /** The study's name as recruiters know it (the internal title first); null outside a study session. */
  studyTitle: string | null;
  /** The list being worked in the bar's words, "<study> recruitment list" (workQueueLogic studyLabelFor). */
  studyLabel: string | null;
  /** Where "Back to the list" goes: the sheet the session started from; null outside a study session. */
  returnTo: string | null;
  /**
   * Work through a study's recruitment list ("Work through" on its sheet).
   * `items` are the rows on screen, already built into queue items in the
   * sheet's order (recruit/recruitLogic rowsToQueueItems); the viewer's own
   * leads go first within each rank, and the page lands on the first.
   * `filters` are the sheet's, for the one silent refill; `returnTo` is the
   * sheet itself. Resolves to the queue size; 0 means nothing was started (no
   * rows, or a study the session could not keep across a refresh).
   */
  startRows: (
    items: WorkQueueItem[],
    study: WorkQueueStudyRef,
    opts?: { returnTo?: string | null; filters?: WorkQueueStudyFilters | null; keepTrying?: number | null },
  ) => Promise<number>;
}

const WorkQueueContext = createContext<WorkQueueCtx>({
  active: false, finished: false, starting: false, item: null, position: 0, total: 0,
  rows: [], done_count: 0, goTo: () => {},
  start: async () => 0, sessionTier: 'now', keepTryingCount: null, startKeepTrying: async () => 0,
  done: () => {}, skip: () => {},
  snoozeCurrent: () => {}, snoozing: false, snoozeError: null, exit: () => {},
  source: null, isListSession: false, listId: null, listName: null,
  startList: async () => 0, listCounts: null, afterOutcome: async () => {},
  newReplies: 0, freshReady: 0, resumeList: () => {}, resumeSkipped: () => {},
  outsideHoursAck: false, ackOutsideHours: () => {}, advanceHeld: false,
  isStudySession: false, studyId: null, studyTitle: null, studyLabel: null, returnTo: null,
  startRows: async () => 0,
});

export function WorkQueueProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  // The viewer's id, only so an explicit owners list can still opt into
  // out-of-office coverage (workQueueLogic.ownersParam explains why).
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const [state, setState] = useState<WorkQueueState | null>(readStored);
  const [finished, setFinished] = useState(false);
  const [starting, setStarting] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);
  const [listCounts, setListCounts] = useState<WorkQueueListCounts | null>(null);
  const [newReplies, setNewReplies] = useState(0);
  const [freshReady, setFreshReady] = useState(0);
  const [outsideHoursAck, setOutsideHoursAck] = useState(false);
  const [advanceHeld, setAdvanceHeld] = useState(false);

  // The poll and afterOutcome both need the LATEST state without re-arming
  // on every change (the poll's interval must not restart on each advance).
  const stateRef = useRef(state);
  stateRef.current = state;
  const ackRef = useRef(outsideHoursAck);
  ackRef.current = outsideHoursAck;
  const countsRef = useRef(listCounts);
  countsRef.current = listCounts;
  /** The last poll's rows — "Keep going" folds these in without a refetch. */
  const lastPollRef = useRef<{ items: WorkQueueItem[]; counts: WorkQueueListCounts | null } | null>(null);

  useEffect(() => { writeStored(state); }, [state]);

  const fetchQueue = useCallback(async (
    source: 'all' | 'safety_net',
    owners: OwnerSelection | undefined,
    tier: WorkQueueTier = 'now',
  ): Promise<{ items: WorkQueueItem[]; keepTrying: number | null }> => {
    const res = await api.get('/api/crm/work-queue', {
      params: {
        ...(source === 'safety_net' ? { source: 'safety_net' } : {}),
        // meId is what lets ownersParam re-assert the coverage default on an
        // EXPLICIT list (see workQueueLogic.ownersParam) — without it the
        // guided flow would silently skip every lead the viewer is covering.
        ...(owners ? { owners: ownersParam(owners, meId) } : {}),
        tier,
      },
    });
    const later = res.data?.tier_counts?.later;
    return {
      items: (res.data?.items || []) as WorkQueueItem[],
      // Only a `now` session offers keep-trying work; an older server sends no counts.
      keepTrying: tier === 'now' && typeof later === 'number' && Number.isFinite(later) ? later : null,
    };
  }, [meId]);

  /** GET /work-queue?list= — items in the server's order plus list_counts. */
  const fetchList = useCallback(async (
    listId: string,
  ): Promise<{ items: WorkQueueItem[]; counts: WorkQueueListCounts | null }> => {
    const res = await api.get('/api/crm/work-queue', { params: { list: listId } });
    return {
      items: (res.data?.items || []) as WorkQueueItem[],
      counts: (res.data?.list_counts as WorkQueueListCounts | undefined) ?? null,
    };
  }, []);

  /**
   * A study session's refill (plan B5): the sheet read again with the filters
   * the session started from, every page up to the sheet's cap (the rows route
   * pages by contact id, so one page is not the top of the list), put in the
   * sheet's needs-first order and built into queue items the way the sheet
   * builds them (never a closed row; the rows with nothing due only when the
   * recruiter asked for them), minus everyone this session already worked or
   * skipped.
   */
  const fetchStudyRefill = useCallback(async (
    s: WorkQueueState,
    tierOverride?: WorkQueueTier,
  ): Promise<{ items: WorkQueueItem[]; keepTrying: number | null }> => {
    const study = s.study;
    if (!study) return { items: [], keepTrying: null };
    const f = s.studyFilters ?? DEFAULT_STUDY_FILTERS;
    const res = await loadAllRecruitRows(study.id, studyRowsParams(f, meId));
    const rows = [...res.rows].sort(compareRows());
    const exclude = sessionExcludes(s);
    const ref = { title: study.title, slug: study.slug };
    const tier = tierOverride ?? tierOf(s);
    const items = rowsToQueueItems(rows, ref, {
      includeWaiting: !tierOverride && f.includeWaiting && !f.onlyNeedsAction,
      tier,
    }).filter((i) => !exclude.has(i.contact_id));
    // The keep-trying leads on the sheet, for a `now` session's offer (never the ones it skipped).
    const keepTrying = tier === 'now'
      ? rowsToQueueItems(rows, ref, { tier: 'later' }).filter((i) => !exclude.has(i.contact_id)).length
      : null;
    return { items, keepTrying };
  }, [meId]);

  /** Clear everything that belongs to one sitting (not the session itself). */
  const resetSitting = useCallback(() => {
    setFinished(false);
    setSnoozeError(null);
    setNewReplies(0);
    setFreshReady(0);
    setAdvanceHeld(false);
    setOutsideHoursAck(false);
    lastPollRef.current = null;
  }, []);

  const start = useCallback(async (
    source?: 'safety_net',
    owners?: OwnerSelection,
    tier: WorkQueueTier = 'now',
  ): Promise<number> => {
    setStarting(true);
    try {
      const { items: fetched, keepTrying } = await fetchQueue(source || 'all', owners, tier);
      if (!fetched.length) return 0;
      // Own work before covered work. withRefill already does this for the
      // silent refill; without it here, the FIRST pass through a widened queue
      // keeps raw server order and a teammate's covered lead can sit ahead of
      // the viewer's own — the rule would appear to switch on halfway through
      // a session, which reads as a bug rather than a policy.
      const items = orderOwnWorkFirst(fetched);
      setState({
        source: source || 'all',
        owners: owners ?? null,
        items,
        index: 0,
        handled: [],
        skipped: [],
        startedAt: new Date().toISOString(),
        tier,
        keepTrying,
      });
      resetSitting();
      setListCounts(null);
      navigate(items[0].href);
      return items.length;
    } catch (e: any) {
      console.error('[workQueue] start failed:', e?.message);
      return 0;
    } finally {
      setStarting(false);
    }
  }, [fetchQueue, navigate, resetSitting]);

  const startList = useCallback(async (listId?: string): Promise<number> => {
    const key = listId ?? 'mine';
    // Refused before the request: the server would 400 anything else, and the
    // value lands in a query string.
    if (!isListParam(key)) return 0;
    setStarting(true);
    // A list session already running on ANOTHER list (the new-list card's
    // "Work through" is not hidden during one) owns `listCounts`: its bar
    // reads them for "42 left" and for its finished sentence. A start that
    // finds nobody leaves that session running, so it must not leave the
    // other list's numbers on its bar either.
    const cur = stateRef.current;
    const keepRunningCounts = cur?.source === 'call_list' && cur.listId !== key;
    try {
      const { items, counts } = await fetchList(key);
      // Nobody servable: do NOT enter the mode. The caller reads listCounts
      // and says when the next tries start instead of opening an empty bar.
      if (!items.length) {
        if (!keepRunningCounts) setListCounts(counts);
        return 0;
      }
      setListCounts(counts);
      setState({
        source: 'call_list',
        owners: null,
        items,
        index: 0,
        handled: [],
        skipped: [],
        startedAt: new Date().toISOString(),
        listId: key,
        listName: listNameFor(items, key),
        remaining: counts?.remaining ?? null,
        doneToday: counts?.done_today ?? null,
      });
      resetSitting();
      navigate(items[0].href);
      return items.length;
    } catch (e: any) {
      console.error('[workQueue] list start failed:', e?.message);
      if (!keepRunningCounts) setListCounts(null);
      return 0;
    } finally {
      setStarting(false);
    }
  }, [fetchList, navigate, resetSitting]);

  const startRows = useCallback(async (
    items: WorkQueueItem[],
    study: WorkQueueStudyRef,
    opts?: { returnTo?: string | null; filters?: WorkQueueStudyFilters | null; keepTrying?: number | null },
  ): Promise<number> => {
    // Without a study it can refill from and name, a session is refused
    // rather than started as one a refresh would drop.
    if (!items.length || !isStudyRef(study)) return 0;
    const base = startRowsState(items, study, { meId });
    if (!survivesRefresh(base)) return 0;
    // sessionStorage also refuses a way back that is not a CRM page and
    // filters the refill could not re-send. Each is let go on its own (the
    // study's own sheet, the sheet's defaults stand in) instead of losing the
    // whole session on a refresh.
    const returnTo = opts?.returnTo ?? null;
    const filters = opts?.filters ?? null;
    const keepTrying = typeof opts?.keepTrying === 'number' && Number.isFinite(opts.keepTrying) ? opts.keepTrying : null;
    const next: WorkQueueState = {
      ...base,
      returnTo: returnTo !== null && survivesRefresh({ ...base, returnTo }) ? returnTo : null,
      studyFilters: filters !== null && survivesRefresh({ ...base, studyFilters: filters }) ? filters : null,
      keepTrying,
    };
    setState(next);
    resetSitting();
    setListCounts(null);
    navigate(next.items[0].href);
    return next.items.length;
  }, [meId, navigate, resetSitting]);

  /** Land the page on the session's current row — unless calling hours hold it. */
  const moveTo = useCallback((next: WorkQueueState, shouldNavigate: boolean) => {
    const target = currentItem(next);
    if (!target) {
      setFinished(true);
      return;
    }
    setFinished(false);
    if (!shouldNavigate) return;
    // Outside 8am–9pm the NEXT call needs a deliberate click-through; the bar
    // shows who is next and holds the page until the recruiter says so.
    if (callingHoursBlock(new Date()) && !ackRef.current) {
      setAdvanceHeld(true);
      return;
    }
    navigate(target.href);
  }, [navigate]);

  /**
   * Advance past the row on deck (Done, Skip, a snooze) — or, from
   * afterOutcome, past a row whose outcome was just logged. `opts` is that
   * outcome path: it lands the way every other outcome does, through moveTo,
   * so `navigate: false` (Call Mode without "Log & next") keeps the page where
   * it is and outside calling hours the page waits for the click-through.
   * Done and Skip pass nothing and land directly, as they always have.
   */
  const step = useCallback(async (skip: boolean, opts?: { navigate?: boolean }) => {
    if (!state) return;
    setSnoozeError(null);
    const land = (s: WorkQueueState, href: string) => {
      if (opts) moveTo(s, opts.navigate !== false);
      else navigate(href);
    };
    const next = skip ? skipState(state) : advanceState(state);
    const nextItem = currentItem(next);
    if (nextItem) {
      setState(next);
      land(next, nextItem.href);
      return;
    }
    // A list session refills from the list, not the whole queue — and folds
    // the answer in the same way an outcome does.
    if (next.source === 'call_list' && next.listId) {
      try {
        const { items, counts } = await fetchList(next.listId);
        setListCounts(counts);
        const refetched = applyListRefetch(next, items, counts);
        setState(refetched);
        setNewReplies(0);
        moveTo(refetched, opts?.navigate !== false);
        return;
      } catch (e: any) {
        console.error('[workQueue] list refill failed:', e?.message);
        setState(next);
        setFinished(true);
        return;
      }
    }
    // A study session refills from its own sheet (plan B5), with the filters
    // it started from. If the recruiter exited, jumped to a row or started
    // something else while the sheet was being read, that is left alone.
    if (next.source === 'study') {
      let fresh: WorkQueueItem[] = [];
      let keepTrying: number | null = next.keepTrying ?? null;
      try {
        const read = await fetchStudyRefill(next);
        fresh = read.items;
        keepTrying = read.keepTrying;
      } catch (e: any) {
        console.error('[workQueue] study refill failed:', e?.message);
      }
      if (stateRef.current !== state) return;
      if (fresh.length) {
        const refilled = { ...withRefill(next, fresh, meId), keepTrying };
        setState(refilled);
        land(refilled, refilled.items[0].href);
        return;
      }
      setState({ ...next, keepTrying });
      setFinished(true);
      return;
    }
    // Exhausted — one silent refill minus everything this session touched.
    // Acted-on leads drop out server-side anyway (their coverage is restored);
    // the exclude set covers the sweep/trigger lag.
    let keepTrying: number | null = next.keepTrying ?? null;
    try {
      const read = await fetchQueue(
        next.source === 'safety_net' ? 'safety_net' : 'all', next.owners ?? undefined, tierOf(next),
      );
      keepTrying = read.keepTrying;
      const exclude = sessionExcludes(next);
      const fresh = read.items.filter((i) => !exclude.has(i.contact_id));
      if (fresh.length) {
        const refilled = { ...withRefill(next, fresh), keepTrying };
        setState(refilled);
        land(refilled, refilled.items[0].href);
        return;
      }
    } catch (e: any) {
      console.error('[workQueue] refill failed:', e?.message);
    }
    setState({ ...next, keepTrying });
    setFinished(true);
  }, [state, meId, fetchQueue, fetchList, fetchStudyRefill, navigate, moveTo]);

  /**
   * The finished state's "Keep trying" (owner 2026-09-28): the same kind of
   * session on the keep-trying tier. A study session re-reads its sheet with
   * the same filters; the others ask the server. Nothing is started when there
   * is nothing to keep trying (resolves 0 and the finished state stays).
   */
  const startKeepTrying = useCallback(async (): Promise<number> => {
    const cur = stateRef.current;
    if (!cur) return 0;
    if (cur.source === 'study') {
      if (!cur.study) return 0;
      setStarting(true);
      try {
        // A fresh sitting: nothing it worked or skipped is carried over.
        const { items } = await fetchStudyRefill({ ...cur, handled: [], skipped: [] }, 'later');
        if (!items.length) {
          setState((prev) => (prev === cur ? { ...cur, keepTrying: 0 } : prev));
          return 0;
        }
        const filters: WorkQueueStudyFilters = {
          ...(cur.studyFilters ?? DEFAULT_STUDY_FILTERS), includeWaiting: false, tier: 'later',
        };
        const base = startRowsState(items, cur.study, { meId });
        const next: WorkQueueState = {
          ...base, returnTo: cur.returnTo ?? null, studyFilters: filters, keepTrying: null,
        };
        setState(next);
        resetSitting();
        navigate(next.items[0].href);
        return next.items.length;
      } catch (e: any) {
        console.error('[workQueue] keep-trying start failed:', e?.message);
        return 0;
      } finally {
        setStarting(false);
      }
    }
    if (cur.source === 'call_list') return 0;
    const count = await start(cur.source === 'safety_net' ? 'safety_net' : undefined, cur.owners ?? undefined, 'later');
    if (!count) setState((prev) => (prev === cur ? { ...cur, keepTrying: 0 } : prev));
    return count;
  }, [fetchStudyRefill, meId, navigate, resetSitting, start]);

  /**
   * An outcome was written for `contactId` — move the session on.
   *
   * Normal session: mark it worked; if it was the row on deck, advance the
   * way Done does (the logged outcome IS the work, so it counts as handled).
   * List session: mark it worked, re-fetch the list, fold it in, land on the
   * server's new top. If the re-fetch fails the local ledger still moves, so
   * a flaky network never serves the same person twice in a sitting.
   */
  const afterOutcome = useCallback(async (contactId: string, opts?: { navigate?: boolean }) => {
    const cur = stateRef.current;
    if (!cur) return;
    const wantNav = opts?.navigate !== false;
    const wasCurrent = currentItem(cur)?.contact_id === contactId;
    const marked = markOutcomeLogged(cur, contactId);

    if (cur.source !== 'call_list' || !cur.listId) {
      // The row on deck was the last one: straight into the usual one silent
      // refill, carrying the navigate choice. No interim setState(marked) —
      // with the cursor past the end it would flash "All clear" for the
      // length of the refill. step's advance records the lead as handled.
      if (wasCurrent && !currentItem(marked)) {
        await step(false, { navigate: wantNav });
        return;
      }
      setState(marked);
      if (wasCurrent) moveTo(marked, wantNav);
      return;
    }

    // No interim setState(marked): on the list's last row it would flash the
    // finished state for the length of the re-fetch.
    try {
      const { items, counts } = await fetchList(cur.listId);
      // The recruiter may have exited or started something else meanwhile.
      const latest = stateRef.current;
      if (!latest || latest.source !== 'call_list' || latest.listId !== cur.listId) return;
      setListCounts(counts);
      const refetched = applyListRefetch(markOutcomeLogged(latest, contactId), items, counts);
      setState(refetched);
      setNewReplies(0);
      setFreshReady(0);
      // A detour lead logged from its own page: the bar moves, the page stays.
      moveTo(refetched, wantNav && wasCurrent);
    } catch (e: any) {
      console.error('[workQueue] list refetch after outcome failed:', e?.message);
      const latest = stateRef.current;
      if (!latest || latest.source !== 'call_list' || latest.listId !== cur.listId) return;
      const local = markOutcomeLogged(latest, contactId);
      setState(local);
      moveTo(local, wantNav && wasCurrent);
    }
  }, [fetchList, moveTo, step]);

  // The list poll: counts + the "new reply" chip, never navigation. Armed per
  // (session, list) — not per state change — so an advance does not restart it.
  const pollSource = state?.source ?? null;
  const pollListId = state?.listId ?? null;
  useEffect(() => {
    if (pollSource !== 'call_list' || !pollListId) return undefined;
    let alive = true;
    let inFlight = false;
    const poll = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        const { items, counts } = await fetchList(pollListId);
        if (!alive) return;
        lastPollRef.current = { items, counts };
        setListCounts(counts);
        const s = stateRef.current;
        setNewReplies(newReplyCount(s, items));
        setFreshReady(freshCount(s, items));
        if (s && counts) {
          // Counts only: the rows and the cursor are left exactly where they
          // are — this runs while someone may be mid-call.
          setState((prev) => (
            prev && prev.listId === pollListId
              && (prev.remaining !== counts.remaining || prev.doneToday !== counts.done_today)
              ? { ...prev, remaining: counts.remaining, doneToday: counts.done_today }
              : prev
          ));
        }
      } catch {
        /* a missed poll is harmless — the next one, or the next outcome, reconciles */
      } finally {
        inFlight = false;
      }
    };
    // A session restored from sessionStorage (a refresh mid-list) has no
    // counts yet; startList already has them, so it does not pay twice.
    if (!countsRef.current) void poll();
    const timer = window.setInterval(poll, LIST_POLL_MS);
    // A lead on one of my lists just texted back: check now, not in a minute.
    const onReply = () => { void poll(); };
    window.addEventListener(OUTREACH_REPLY_EVENT, onReply);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener(OUTREACH_REPLY_EVENT, onReply);
    };
  }, [pollSource, pollListId, fetchList]);

  /** "Keep going" from the finished state: fold in what the poll found. */
  const resumeList = useCallback(() => {
    const cur = stateRef.current;
    const polled = lastPollRef.current;
    if (!cur || cur.source !== 'call_list' || !polled) return;
    const refetched = applyListRefetch(cur, polled.items, polled.counts);
    setState(refetched);
    setNewReplies(0);
    setFreshReady(0);
    moveTo(refetched, true);
  }, [moveTo]);

  /**
   * The finished state's "Go back to them": the recruiter skipped people who
   * are still due, and the list ran out because of it. Skip means "not now"
   * for the sitting — this is the recruiter saying "now", so the sitting's
   * skips are forgiven and the list is refilled with them back in it, in the
   * server's order. The skips are only dropped in the fold: a failed fetch
   * leaves the finished state (and its offer) exactly as it was.
   */
  const resumeSkipped = useCallback(async () => {
    const cur = stateRef.current;
    if (!cur || cur.source !== 'call_list' || !cur.listId) return;
    try {
      const { items, counts } = await fetchList(cur.listId);
      const latest = stateRef.current;
      if (!latest || latest.source !== 'call_list' || latest.listId !== cur.listId) return;
      setListCounts(counts);
      const refetched = applyListRefetch({ ...latest, skipped: [] }, items, counts);
      setState(refetched);
      setNewReplies(0);
      setFreshReady(0);
      moveTo(refetched, true);
    } catch (e: any) {
      console.error('[workQueue] going back to skipped people failed:', e?.message);
    }
  }, [fetchList, moveTo]);

  const ackOutsideHours = useCallback(() => {
    setOutsideHoursAck(true);
    ackRef.current = true;
    const target = currentItem(stateRef.current);
    if (advanceHeld && target) navigate(target.href);
    setAdvanceHeld(false);
  }, [advanceHeld, navigate]);

  /**
   * Snooze the lead on deck, then advance. The advance runs ONLY after the
   * PATCH lands: moving on from a lead we failed to defer would hand them
   * straight back on the next refill, which is the whole thing this fixes.
   * Counted as `handled` (not skipped) — deciding a lead needs nothing IS
   * working it.
   */
  const snoozeCurrent = useCallback(async (untilIso: string) => {
    const cur = currentItem(state);
    if (!cur || snoozing) return;
    setSnoozing(true);
    setSnoozeError(null);
    try {
      await api.patch(`/api/crm/contacts/${cur.contact_id}`, { snoozed_until: untilIso });
    } catch (e: any) {
      console.error('[workQueue] snooze failed:', e?.message);
      setSnoozeError('Could not snooze this lead — try again.');
      return;
    } finally {
      setSnoozing(false);
    }
    await step(false);
  }, [state, snoozing, step]);

  const exit = useCallback((goHome: boolean | string = false) => {
    setState(null);
    resetSitting();
    setListCounts(null);
    writeStored(null);
    if (typeof goHome === 'string') navigate(goHome);
    else if (goHome) navigate('/crm');
  }, [navigate, resetSitting]);

  /** Jump to a row the recruiter picked out of the list. Marks nothing —
   *  browsing the queue is not the same as working it. */
  const goTo = useCallback((index: number) => {
    if (!state) return;
    const target = state.items[index];
    if (!target) return;
    setState({ ...state, index });
    setFinished(false);
    setAdvanceHeld(false);
    navigate(target.href);
  }, [state, navigate]);

  const { position, total } = progress(state);
  // Set only while a study session runs (parseStoredState and startRows both
  // refuse a study session without its study).
  const studyRef = state?.source === 'study' ? state.study ?? null : null;
  const rows = state
    ? state.items.map((item, i) => ({ item, status: rowStatus(state, i) }))
    : [];
  return (
    <WorkQueueContext.Provider
      value={{
        active: !!state,
        // A cursor past the end IS finished, whether or not this mount saw it
        // happen — a refresh on the finished state restores exactly that.
        finished: finished || (!!state && !currentItem(state)),
        starting,
        item: currentItem(state),
        position,
        total,
        rows,
        done_count: doneCount(state),
        goTo,
        start,
        sessionTier: tierOf(state),
        keepTryingCount: state && tierOf(state) === 'now' ? state.keepTrying ?? null : null,
        startKeepTrying,
        done: () => { void step(false); },
        skip: () => { void step(true); },
        snoozeCurrent: (untilIso: string) => { void snoozeCurrent(untilIso); },
        snoozing,
        snoozeError,
        exit,
        source: state?.source ?? null,
        isListSession: state?.source === 'call_list',
        listId: state?.listId ?? null,
        listName: state?.listName ?? null,
        startList,
        listCounts,
        afterOutcome,
        newReplies,
        freshReady,
        resumeList,
        resumeSkipped: () => { void resumeSkipped(); },
        outsideHoursAck,
        ackOutsideHours,
        advanceHeld,
        isStudySession: state?.source === 'study',
        studyId: studyRef?.id ?? null,
        studyTitle: studyRef?.title ?? null,
        studyLabel: studyRef ? studyLabelFor(state) : null,
        returnTo: studyRef ? studyReturnHref(state) : null,
        startRows,
      }}
    >
      {children}
    </WorkQueueContext.Provider>
  );
}

export function useWorkQueue(): WorkQueueCtx {
  return useContext(WorkQueueContext);
}
